import { motion } from 'framer-motion';
import { useEffect } from 'react';

/**
 * Facebook Page integration section.
 * Displays embedded Facebook Page plugin with recent posts.
 * Facebook Page: https://www.facebook.com/profile.php/?id=100086623726840
 */

// Facebook Page URL for Nanny B's Bakery
const FACEBOOK_PAGE_URL = 'https://www.facebook.com/profile.php/?id=100086623726840';

export default function FacebookSection() {
  // Load Facebook SDK
  useEffect(() => {
    // Check if FB SDK is already loaded
    if ((window as any).FB) {
      (window as any).FB.XFBML.parse();
      return;
    }

    // Load Facebook SDK
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v18.0';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    document.body.appendChild(script);

    // Initialize FB SDK when loaded
    (window as any).fbAsyncInit = function() {
      (window as any).FB.init({
        xfbml: true,
        version: 'v18.0'
      });
    };

    return () => {
      // Cleanup if needed
    };
  }, []);

  return (
    <section className="section-padding bg-gradient-to-b from-white to-bakery-cream">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
            Follow Us on Facebook
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Stay updated with our latest creations, specials, and behind-the-scenes moments!
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Facebook Page Plugin */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <div className="bg-white rounded-xl shadow-lg p-4 overflow-hidden">
              <div 
                className="fb-page" 
                data-href={FACEBOOK_PAGE_URL}
                data-tabs="timeline"
                data-width="400" 
                data-height="500"
                data-small-header="false"
                data-adapt-container-width="true"
                data-hide-cover="false"
                data-show-facepile="true"
              >
                {/* Fallback while loading */}
                <blockquote 
                  cite={FACEBOOK_PAGE_URL}
                  className="fb-xfbml-parse-ignore"
                >
                  <a href={FACEBOOK_PAGE_URL}>Nanny B's Bakery on Facebook</a>
                </blockquote>
              </div>
            </div>
          </motion.div>

          {/* CTA and Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex flex-col justify-center space-y-6"
          >
            {/* Why Follow Us */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">📸</span>
                <div>
                  <h3 className="font-semibold text-gray-900">Fresh Photos Daily</h3>
                  <p className="text-gray-600">See our latest cakes and treats as they come out of the oven</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">🎉</span>
                <div>
                  <h3 className="font-semibold text-gray-900">Special Offers</h3>
                  <p className="text-gray-600">Be the first to know about seasonal specials and promotions</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">💬</span>
                <div>
                  <h3 className="font-semibold text-gray-900">Easy Ordering</h3>
                  <p className="text-gray-600">Message us directly on Facebook to place custom orders</p>
                </div>
              </div>
            </div>

            {/* Follow Button */}
            <div className="pt-4">
              <a
                href={FACEBOOK_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#1877F2] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#166FE5] transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Follow Nanny B's Bakery
              </a>
            </div>

            {/* Social Proof */}
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <span className="text-red-500">❤️</span>
              Join our growing community of baked goods lovers!
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

